import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { API_URLS } from '../../../config/api';
import { Bot, RefreshCcw, Database, Zap, TrendingUp, AlertCircle, CheckCircle, Save, Settings, Plus, Trash2 } from 'lucide-react';

const AdminGogiAI = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [crawling, setCrawling] = useState(false);
  
  // Settings states
  const [settings, setSettings] = useState({
    // Intelligent Prompt Template
    intelligent_prompt_template: '',
    // Selamlaşma
    greeting_response: '',
    greeting_how_are_you: '',
    greeting_thanks: '',
    greeting_goodbye: '',
    // Genel Yanıtlar
    general_ecommerce: '',
    general_hosting: '',
    general_web: '',
    general_default: '',
    // System Prompt
    system_prompt: '',
    // RAG Gelişmiş Ayarlar
    rag_enable_general_category_check: '1',
    rag_enable_package_info_request: '1',
    rag_enable_package_purchase_request: '1',
    rag_auto_create_contact_request: '1',
    rag_show_contact_button: '1',
    rag_show_purchase_button: '1',
    rag_enable_suggestions: '1'
  });
  const [sectorPrompts, setSectorPrompts] = useState([]);
  
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptMessage, setPromptMessage] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);

  const loadStats = async () => {
    setLoading(true);
    try {
      const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';
      const res = await fetch(`${apiBase}/backend/api.php?action=gogiai_stats`);
      const data = await res.json();
      
      // Esnek cevap işleme: {status, data:{stats, openai_configured}} veya doğrudan {stats, openai_configured}
      const payload = data?.status === 'success' ? (data?.data ?? {}) : data;
      const merged = payload?.stats ? { ...payload.stats, openai_configured: payload.openai_configured } : payload;
      if (merged) setStats(merged);
    } catch (e) {
      console.error('Stats yükleme hatası:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    setPromptLoading(true);
    try {
      const res = await fetch(API_URLS.ADMIN.ADMIN_API + '?action=getGogiaiSettings');
      const data = await res.json();
      
      if (data.status === 'success' && data.data) {
        const incoming = data.data;
        setSettings(prevSettings => ({
          ...prevSettings,
          ...incoming
        }));

        if (incoming.sector_prompts) {
          try {
            const parsed = typeof incoming.sector_prompts === 'string'
              ? JSON.parse(incoming.sector_prompts)
              : incoming.sector_prompts;
            setSectorPrompts(Array.isArray(parsed) ? parsed : []);
          } catch (error) {
            console.warn('sector_prompts parse error', error);
            setSectorPrompts([]);
          }
        } else {
          setSectorPrompts([]);
        }
      } else {
        setPromptMessage(`❌ API Error: ${data.message || 'Unknown error'}`);
      }
    } catch (e) {
      console.error('Settings yükleme hatası:', e);
      setPromptMessage('❌ Ayarlar yüklenemedi: ' + e.message);
    } finally {
      setPromptLoading(false);
    }
  };

  const handleSaveSetting = async (key, value) => {
    setSavingPrompt(true);
    setPromptMessage('');
    try {
      const res = await fetch(API_URLS.ADMIN.ADMIN_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'updateGogiaiSettings',
          setting_key: key,
          setting_value: typeof value === 'object' ? JSON.stringify(value) : value
        })
      });
      const data = await res.json();
      
      if (data.status === 'success') {
        setPromptMessage(`✅ ${key} başarıyla kaydedildi!`);
        setTimeout(() => setPromptMessage(''), 3000);
      } else {
        setPromptMessage(`❌ Hata: ${data.message || 'Bilinmeyen hata'}`);
      }
    } catch (e) {
      setPromptMessage(`❌ Bağlantı hatası: ${e.message}`);
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleAddSectorPrompt = () => {
    setSectorPrompts((prev) => [...prev, { sector: '', prompt: '' }]);
  };

  const handleUpdateSectorPrompt = (index, key, value) => {
    setSectorPrompts((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [key]: value
      };
      return updated;
    });
  };

  const handleRemoveSectorPrompt = (index) => {
    setSectorPrompts((prev) => prev.filter((_, idx) => idx !== index));
  };
  
  const handleSaveAllSettings = async () => {
    setSavingPrompt(true);
    setPromptMessage('');
    
    try {
      // Tüm settings'leri sırayla kaydet
      const settingsToSave = {
        ...settings,
        sector_prompts: sectorPrompts
      };

      for (const [key, value] of Object.entries(settingsToSave)) {
        if (value) {
          await fetch(API_URLS.ADMIN.ADMIN_API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'updateGogiaiSettings',
              setting_key: key,
              setting_value: typeof value === 'object' ? JSON.stringify(value) : value
            })
          });
        }
      }
      
      setPromptMessage('✅ Tüm ayarlar başarıyla kaydedildi!');
      setTimeout(() => setPromptMessage(''), 3000);
    } catch (e) {
      setPromptMessage(`❌ Kaydetme hatası: ${e.message}`);
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleLoadFromDatabase = async (clearExisting = false) => {
    setCrawling(true);
    setMessage('');
    try {
      const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';
      const res = await fetch(`${apiBase}/backend/api.php?action=gogiai_load_from_database`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clear_existing: clearExisting })
      });
      const data = await res.json();

      // Esnek yanıt çözümleme
      const payload = data?.status === 'success' ? (data?.data ?? {}) : (data?.data ?? data ?? {});
      const total = payload?.total ?? 0;
      const added = payload?.added ?? 0;
      const failed = payload?.failed ?? 0;
      const success = (data?.status === 'success') || (typeof total === 'number' && typeof added === 'number') || /başar/i.test(data?.message || '');

      if (success) {
        setMessage(`✅ Başarılı! ${total} sayfa bulundu, ${added} içerik eklendi${failed > 0 ? `, ${failed} başarısız` : ''}.`);
        loadStats();
      } else {
        setMessage(`❌ Hata: ${data?.message || 'Bilinmeyen hata'}`);
      }
    } catch (e) {
      setMessage(`❌ Bağlantı hatası: ${e.message}`);
    } finally {
      setCrawling(false);
    }
  };

  useEffect(() => {
    loadStats();
    loadSettings();
  }, []);

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">GogiAI Yönetimi</h1>
          <p className="text-gray-600 mt-2">AI asistanının bilgi tabanını yönetin</p>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={loadStats} disabled={loading}>
            <RefreshCcw className="w-4 h-4 mr-2"/>
            Yenile
          </Button>
        </div>
      </div>

      {/* İstatistikler */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Toplam İçerik</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_content ?? 0}</div>
            <p className="text-xs text-muted-foreground">
              Kayıtlı içerik
              {stats?.total_expected_content && stats.total_expected_content > 0 && (
                <span className="text-gray-500"> / {stats.total_expected_content} beklenen</span>
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Embedding'ler</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_embeddings ?? 0}</div>
            <p className="text-xs text-muted-foreground">Vektörleştirilmiş</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Kapsam</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.total_content && stats?.total_expected_content && stats.total_expected_content > 0
                ? Math.round((stats.total_content / stats.total_expected_content) * 100) 
                : 0}%
            </div>
            <p className="text-xs text-muted-foreground">İşlenmiş içerik</p>
          </CardContent>
        </Card>
      </div>

      {/* İçerik Yönetimi */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Bot className="w-5 h-5" />
            <span>Bilgi Tabanı Yönetimi</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* İçerik Durumu Bilgisi */}
          {stats && (() => {
            const isMatched = stats.total_content > 0 && stats.total_expected_content > 0 && stats.total_content === stats.total_expected_content;
            const bgColor = isMatched ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200';
            const textColor = isMatched ? 'text-green-700' : 'text-amber-700';
            const titleColor = isMatched ? 'text-green-900' : 'text-amber-900';
            const iconColor = isMatched ? 'text-green-600' : 'text-amber-600';
            const Icon = isMatched ? CheckCircle : AlertCircle;
            
            return (
              <div className={`${bgColor} border rounded-lg p-4`}>
                <div className="flex items-start space-x-3">
                  <Icon className={`w-5 h-5 ${iconColor} mt-0.5`} />
                  <div className="flex-1">
                    <h4 className={`font-semibold ${titleColor}`}>İçerik Durumu</h4>
                    <div className={`text-sm ${textColor} mt-2 space-y-1`}>
                      {isMatched ? (
                        <p>
                          ✅ Tüm içerikler başarıyla yüklendi. RAG sisteminde <strong>{stats.total_content || 0}</strong> içerik kayıtlı 
                          ({stats.active_pages_count || 0} sayfa, {stats.active_blogs_count || 0} blog, {stats.active_faqs_count || 0} FAQ, {stats.active_educations_count || 0} eğitim).
                        </p>
                      ) : (
                        <>
                          <p>• RAG sisteminde <strong>{stats.total_content || 0}</strong> içerik kayıtlı</p>
                          <p>• Veritabanında <strong>{stats.total_expected_content || 0}</strong> içerik mevcut 
                            ({stats.active_pages_count || 0} sayfa, {stats.active_blogs_count || 0} blog, {stats.active_faqs_count || 0} FAQ, {stats.active_educations_count || 0} eğitim)</p>
                          {stats.total_content > 0 && stats.total_expected_content > 0 && stats.total_content !== stats.total_expected_content && (
                            <p className={`text-amber-800 font-medium`}>
                              ⚠️ İçerik sayısı eşleşmiyor. RAG sisteminde {stats.total_content} içerik vektörleştirilmiş, ancak veritabanında {stats.total_expected_content} içerik mevcut. 
                              Bu fark, bazı içeriklerin embedding oluşturulurken başarısız olması veya yeni içeriklerin henüz vektörleştirilmemesi nedeniyle oluşabilir. 
                              "Bilgi Tabanını Güncelle" butonunu kullanarak tüm içerikleri temizleyip veritabanındaki güncel içerikleri yeniden yükleyebilirsiniz.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Database className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-blue-900">Bilgi Tabanını Güncelle</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Veritabanındaki aktif sayfa içeriklerini (page_contents tablosu) RAG sistemine yükler. 
                  Mevcut içerikler temizlenir ve sadece veritabanındaki güncel sayfalar eklenir.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4 flex-wrap gap-2">
            <Button 
              onClick={() => {
                if (window.confirm('Tüm mevcut içerikler silinecek ve veritabanındaki aktif sayfalar yüklenecek. Emin misiniz?')) {
                  handleLoadFromDatabase(true);
                }
              }} 
              disabled={crawling}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            >
              {crawling ? (
                <>
                  <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                  Güncelleniyor...
                </>
              ) : (
                <>
                  <Database className="w-4 h-4 mr-2" />
                  Bilgi Tabanını Güncelle
                </>
              )}
            </Button>
            
            <div className="text-sm text-gray-600">
              Son güncelleme: {stats?.last_crawl || 'Henüz yapılmadı'}
            </div>
          </div>

          {message && (
            <div className={`p-3 rounded-lg text-sm ${
              message.includes('✅') 
                ? 'bg-green-50 text-green-700 border border-green-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {message}
            </div>
          )}
        </CardContent>
      </Card>

      {/* RAG Sistem Ayarları */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="w-5 h-5" />
            <span>RAG Sistem Ayarları</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Intelligent Prompt Düzenleme */}
          <div>
            <div className="space-y-4 mt-6">
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <h4 className="font-semibold text-indigo-900 mb-2">Intelligent Prompt Template</h4>
                <p className="text-sm text-indigo-700">
                  Bu prompt'a müşteri bilgileri, conversation history, paketler ve kategoriler otomatik eklenir.
                  ChatGPT'ye gönderilen tam prompt'u burada düzenleyebilirsiniz.
                </p>
                <p className="text-xs text-indigo-600 mt-2">
                  💡 Dinamik bilgiler (müşteri adı, sektör, paketler, vb.) otomatik olarak eklenir.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Intelligent Prompt Template *
                </label>
                <textarea
                  value={settings.intelligent_prompt_template}
                  onChange={(e) => setSettings({...settings, intelligent_prompt_template: e.target.value})}
                  disabled={promptLoading}
                  className="w-full h-[500px] p-4 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                  placeholder={`Sen Gogi, Website Al'ın AI asistanısın. Müşterilere web hosting, tasarım ve dijital çözümler hakkında yardımcı oluyorsun.

=== MÜŞTERİ BİLGİLERİ ===
[Otomatik eklenir]

=== KONUŞMA GEÇMİŞİ ===
[Otomatik eklenir]

=== MEVCUT PAKETLER ===
[Otomatik eklenir]

=== TALİMATLAR ===
1. Müşteri ilk mesajını attığında: Merhaba de ve isteği ile alakalı sorular sor.
2. Hangi kategori belirtmiş mi kontrol et:
   - Belirtmişse: O kategoriye özel paketleri göster ve detaylı bilgi ver.
   - Belirtmemişse: Kategorileri listele ve hangisini istediğini sor.
...`}
                />
                <p className="text-xs text-gray-500 mt-2">
                  {(settings.intelligent_prompt_template || '').length} karakter
                </p>
                <div className="mt-2 flex items-center space-x-2">
                  <Button
                    onClick={() => handleSaveSetting('intelligent_prompt_template', settings.intelligent_prompt_template)}
                    disabled={savingPrompt}
                    size="sm"
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {savingPrompt ? (
                      <>
                        <RefreshCcw className="w-3 h-3 mr-2 animate-spin" />
                        Kaydediliyor...
                      </>
                    ) : (
                      <>
                        <Save className="w-3 h-3 mr-2" />
                        Intelligent Prompt'u Kaydet
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>


          {/* Eski sistem kaldırıldı - Artık sadece Intelligent Prompt kullanılıyor */}
          {false && (
            <>
              <div className="flex space-x-2 border-b border-gray-200">
            <button
              onClick={() => setActiveSection('greeting')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeSection === 'greeting'
                  ? 'border-b-2 border-purple-600 text-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              1️⃣ Selamlaşma
            </button>
            <button
              onClick={() => setActiveSection('general')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeSection === 'general'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              2️⃣ Genel Yanıtlar
            </button>
            <button
              onClick={() => setActiveSection('system')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeSection === 'system'
                  ? 'border-b-2 border-green-600 text-green-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              3️⃣ System Prompt (GPT)
            </button>
            <button
              onClick={() => setActiveSection('sector')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeSection === 'sector'
                  ? 'border-b-2 border-amber-500 text-amber-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              4️⃣ Sektör Prompts
            </button>
            <button
              onClick={() => setActiveSection('advanced')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeSection === 'advanced'
                  ? 'border-b-2 border-indigo-600 text-indigo-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              5️⃣ Gelişmiş Ayarlar
            </button>
          </div>

          {/* Selamlaşma Yanıtları */}
          {activeSection === 'greeting' && (
            <div className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-900">Selamlaşma Yanıtları</h4>
                <p className="text-sm text-purple-700 mt-1">
                  Müşteri selamlaştığında, teşekkür ettiğinde veya veda ettiğinde bot'un vereceği yanıtları düzenleyin.
                  {'{'}İsim{'}'} ve {'{'}Bey/Hanım{'}'} otomatik eklenir.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  "Merhaba" Yanıtı
                </label>
                <textarea
                  value={settings.greeting_response}
                  onChange={(e) => setSettings({...settings, greeting_response: e.target.value})}
                  className="w-full h-32 p-4 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Merhaba! 😊 Website Al ile ilgili..."
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  "Nasılsın?" Yanıtı
                </label>
                <textarea
                  value={settings.greeting_how_are_you}
                  onChange={(e) => setSettings({...settings, greeting_how_are_you: e.target.value})}
                  className="w-full h-32 p-4 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="İyiyim, teşekkür ederim! 🙌..."
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  "Teşekkür ederim" Yanıtı
                </label>
                <textarea
                  value={settings.greeting_thanks}
                  onChange={(e) => setSettings({...settings, greeting_thanks: e.target.value})}
                  className="w-full h-32 p-4 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Rica ederim! ✨..."
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  "Görüşürüz" Yanıtı
                </label>
                <textarea
                  value={settings.greeting_goodbye}
                  onChange={(e) => setSettings({...settings, greeting_goodbye: e.target.value})}
                  className="w-full h-32 p-4 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Görüşmek üzere! 👋..."
                />
              </div>
            </div>
          )}

          {/* Genel Yanıtlar */}
          {activeSection === 'general' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900">Genel Yanıtlar</h4>
                <p className="text-sm text-blue-700 mt-1">
                  E-ticaret, Hosting, Web gibi genel sorulara verilecek yanıtları düzenleyin.
                  Bu yanıtlar GPT'ye gitmeden direkt döner.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  E-Ticaret Soruları İçin
                </label>
                <textarea
                  value={settings.general_ecommerce}
                  onChange={(e) => setSettings({...settings, general_ecommerce: e.target.value})}
                  className="w-full h-40 p-4 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="E-ticaret konusunda size yardımcı olabilirim! 🛒..."
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Hosting Soruları İçin
                </label>
                <textarea
                  value={settings.general_hosting}
                  onChange={(e) => setSettings({...settings, general_hosting: e.target.value})}
                  className="w-full h-40 p-4 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Hosting hizmetlerimiz hakkında bilgi verebilirim! 🖥️..."
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Web Tasarım Soruları İçin
                </label>
                <textarea
                  value={settings.general_web}
                  onChange={(e) => setSettings({...settings, general_web: e.target.value})}
                  className="w-full h-40 p-4 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Web tasarım ve geliştirme hizmetlerimiz var! 💻..."
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Varsayılan Yanıt (Belirsiz Sorular)
                </label>
                <textarea
                  value={settings.general_default}
                  onChange={(e) => setSettings({...settings, general_default: e.target.value})}
                  className="w-full h-40 p-4 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Bu konuda size yardımcı olabilirim! 🤝..."
                />
              </div>
            </div>
          )}

          {/* System Prompt */}
          {activeSection === 'system' && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900">System Prompt (GPT Talimatları)</h4>
                <p className="text-sm text-green-700 mt-1">
                  OpenAI GPT modeline gönderilen talimatlar. Bot'un davranışını, kişiliğini ve yanıt tarzını belirler.
                  Detaylı sorularda GPT bu talimatlara göre yanıt verir.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Sistem Prompt (AI Davranış Talimatları)
                </label>
                <textarea
                  value={settings.system_prompt}
                  onChange={(e) => setSettings({...settings, system_prompt: e.target.value})}
                  disabled={promptLoading}
                  className="w-full h-96 p-4 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                  placeholder="Sen Gogi, Website Al'ın AI asistanısın..."
                />
                <p className="text-xs text-gray-500 mt-2">
                  {(settings.system_prompt || '').length} karakter
                </p>
              </div>
            </div>
          )}

          {/* Sector Prompts */}
          {activeSection === 'sector' && (
            <div className="space-y-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold text-amber-900">Sektör Bazlı Promptlar</h4>
                <p className="text-sm text-amber-700 mt-1">
                  Farklı sektörlere özel ton ve içerik ayarlamak için sektör adını ve GPT'ye gönderilecek talimatı yazın.
                  Bot, kullanıcının sektörü biliniyorsa bu üst katman promptu uygular.
                </p>
              </div>

              <div className="space-y-4">
                {sectorPrompts.length === 0 && (
                  <div className="text-sm text-gray-600 bg-gray-50 border border-dashed border-gray-300 rounded-lg p-6 text-center">
                    Henüz sektör promptu eklemediniz. Aşağıdaki butonla yeni sektör oluşturabilirsiniz.
                  </div>
                )}

                {sectorPrompts.map((item, index) => (
                  <div key={index} className="border border-amber-200 rounded-lg p-4 space-y-3 bg-white shadow-sm">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div className="flex-1">
                        <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Sektör Adı</label>
                        <input
                          type="text"
                          value={item.sector}
                          onChange={(e) => handleUpdateSectorPrompt(index, 'sector', e.target.value)}
                          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:ring-amber-500"
                          placeholder="Örn: E-ticaret / Sağlık / Turizm"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleRemoveSectorPrompt(index)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Sil
                      </Button>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Prompt</label>
                      <textarea
                        value={item.prompt}
                        onChange={(e) => handleUpdateSectorPrompt(index, 'prompt', e.target.value)}
                        className="mt-1 w-full min-h-[140px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:ring-amber-500"
                        placeholder={'Bu sektör için GPT’ye verilecek ekstra bağlam. Örn:\n- Tonu daha kurumsal tut\n- X metriklerine değin\n- Sektöre özel örnekler ver'}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button type="button" variant="outline" onClick={handleAddSectorPrompt} className="border-amber-300 text-amber-600 hover:bg-amber-50">
                  <Plus className="w-4 h-4 mr-2" />
                  Yeni Sektör Promptu Ekle
                </Button>
                <Button
                  type="button"
                  onClick={() => handleSaveSetting('sector_prompts', sectorPrompts)}
                  disabled={savingPrompt}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                >
                  {savingPrompt ? (
                    <>
                      <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                      Kaydediliyor...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Sektör Promptlarını Kaydet
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Intelligent Prompt Modu İçin Özel Ayarlar */}
          {settings.rag_use_intelligent_prompt === '1' && (
            <div className="space-y-6">
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <h4 className="font-semibold text-indigo-900">Intelligent Prompt Modu</h4>
                <p className="text-sm text-indigo-700 mt-1">
                  Bu modda sistem tüm context'i (müşteri bilgileri, conversation history, paketler, kategoriler) 
                  ChatGPT'ye gönderir ve o karar verir. Bu yüzden sadece temel ayarlar düzenlenebilir.
                </p>
              </div>

              {/* System Prompt - Intelligent mod için önemli */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  System Prompt (AI Talimatları) *
                </label>
                <textarea
                  value={settings.system_prompt}
                  onChange={(e) => setSettings({...settings, system_prompt: e.target.value})}
                  disabled={promptLoading}
                  className="w-full h-96 p-4 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                  placeholder="Sen Gogi, Website Al'ın AI asistanısın..."
                />
                <p className="text-xs text-gray-500 mt-2">
                  {(settings.system_prompt || '').length} karakter
                </p>
                <p className="text-xs text-indigo-600 mt-1">
                  💡 Bu prompt'a müşteri bilgileri, conversation history, paketler ve kategoriler otomatik eklenir.
                </p>
              </div>

              {/* Selamlaşma - Basit versiyon */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Selamlaşma Yanıtı
                </label>
                <textarea
                  value={settings.greeting_response}
                  onChange={(e) => setSettings({...settings, greeting_response: e.target.value})}
                  className="w-full h-32 p-4 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Merhaba! 😊 Website Al ile ilgili..."
                />
              </div>
            </div>
          )}

          {/* Gelişmiş Ayarlar - Kaldırıldı */}
          {false && (
            <div className="space-y-6">
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <h4 className="font-semibold text-indigo-900">RAG Gelişmiş Özellikler</h4>
                <p className="text-sm text-indigo-700 mt-1">
                  Son eklenen RAG sistem özelliklerini açıp kapatabilirsiniz. Bu özellikler bot'un davranışını kontrol eder.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white">
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">Genel Kategori Kontrolü</h5>
                    <p className="text-sm text-gray-600 mt-1">
                      Müşteri genel bir kategori sorduğunda (örn: "hosting"), alt kategorileri listeler ve seçim yaptırır.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      checked={settings.rag_enable_general_category_check === '1'}
                      onChange={(e) => setSettings({...settings, rag_enable_general_category_check: e.target.checked ? '1' : '0'})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white">
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">Paket Bilgi Talebi Algılama</h5>
                    <p className="text-sm text-gray-600 mt-1">
                      Müşteri paket hakkında bilgi istediğinde (örn: "e-Başlangıç hakkında bilgi"), paket detaylarını gösterir.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      checked={settings.rag_enable_package_info_request === '1'}
                      onChange={(e) => setSettings({...settings, rag_enable_package_info_request: e.target.checked ? '1' : '0'})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white">
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">Paket Satın Alma Talebi Algılama</h5>
                    <p className="text-sm text-gray-600 mt-1">
                      Müşteri paket satın almak istediğinde (örn: "e-Başlangıç paketini almak istiyorum"), satın alma butonu gösterir.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      checked={settings.rag_enable_package_purchase_request === '1'}
                      onChange={(e) => setSettings({...settings, rag_enable_package_purchase_request: e.target.checked ? '1' : '0'})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white">
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">Otomatik Talep Oluşturma</h5>
                    <p className="text-sm text-gray-600 mt-1">
                      Paket bilgi talebi algılandığında, müşteri bilgisi varsa otomatik olarak quote_requests tablosuna kayıt oluşturur.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      checked={settings.rag_auto_create_contact_request === '1'}
                      onChange={(e) => setSettings({...settings, rag_auto_create_contact_request: e.target.checked ? '1' : '0'})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white">
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">İletişim Talebi Butonu</h5>
                    <p className="text-sm text-gray-600 mt-1">
                      Paket bilgi talebi algılandığında "İletişim Talebi Oluştur" butonunu gösterir (müşteri bilgisi varsa).
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      checked={settings.rag_show_contact_button === '1'}
                      onChange={(e) => setSettings({...settings, rag_show_contact_button: e.target.checked ? '1' : '0'})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white">
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">Satın Alma Butonu</h5>
                    <p className="text-sm text-gray-600 mt-1">
                      Paket satın alma talebi algılandığında veya paket bilgisi gösterildiğinde "Paketi Satın Al" butonunu gösterir.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      checked={settings.rag_show_purchase_button === '1'}
                      onChange={(e) => setSettings({...settings, rag_show_purchase_button: e.target.checked ? '1' : '0'})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white">
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">Suggestion Butonları</h5>
                    <p className="text-sm text-gray-600 mt-1">
                      Bot yanıtlarının altında önerilen sorular ve linkler gösterilir (örn: "başlangıç paketi hakkında bilgi", "E-ticaret sayfası").
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      checked={settings.rag_enable_suggestions === '1'}
                      onChange={(e) => setSettings({...settings, rag_enable_suggestions: e.target.checked ? '1' : '0'})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
              </div>
            </div>
          )}
            </>
          )}

          {/* Refresh Button */}
          <div className="flex items-center justify-end pt-4 border-t border-gray-200">
            <Button 
              onClick={loadSettings} 
              disabled={promptLoading}
              variant="outline"
            >
              <RefreshCcw className="w-4 h-4 mr-2" />
              Yenile
            </Button>
          </div>

          {promptMessage && (
            <div className={`p-3 rounded-lg text-sm ${
              promptMessage.includes('✅') 
                ? 'bg-green-50 text-green-700 border border-green-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {promptMessage}
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Ayarları */}
      <Card>
        <CardHeader>
          <CardTitle>AI Ayarları</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">OpenAI API Anahtarı</label>
              <div className="mt-1">
                <Badge variant={stats?.openai_configured ? 'default' : 'destructive'}>
                  {stats?.openai_configured ? 'Yapılandırılmış' : 'Yapılandırılmamış'}
                </Badge>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Model</label>
              <div className="mt-1 text-sm text-gray-600">GPT-4o-mini (Maliyet Optimizasyonu)</div>
            </div>
          </div>

          {!stats?.openai_configured && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-yellow-900">OpenAI API Anahtarı Gerekli</h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    GogiAI'nin çalışması için OpenAI API anahtarı gereklidir.
                    Sunucudaki .env dosyasına OPENAI_API_KEY değişkeni olarak ekleyin.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminGogiAI;


